import { differenceInMinutes, eachDay, format } from "date-fns";
import {
  filter,
  groupBy,
  map,
  mapObjIndexed,
  partial,
  pipe,
  prop,
  reduce,
  sum,
} from "ramda";
import { Maybe } from "true-myth";

import { DateFormats } from "helpers/dates";
import {
  isBlockOnDate,
  getAssigneeId,
  getPeopleShifts,
} from "helpers/schedule/shifts";
import {
  Category,
  IPeople,
  IPerson,
  IRole,
  ISchedule,
  IShift,
} from "types/schedules";

interface IGroupedCosts {
  [key: string]: number;
}

interface IGroupedShifts {
  [key: string]: IShift[];
}

interface IGroupedPeople {
  [key: string]: IPeople;
}

function getPayRateValue(role: IRole): number {
  return role.payRate.match({
    Just: payRate => payRate.toDollars(),
    Nothing: () => 0,
  });
}

function calculateDurationInHours(shift: IShift): number {
  return differenceInMinutes(shift.endTime, shift.startTime) / 60;
}

function getShiftCost(shift: IShift): number {
  return calculateDurationInHours(shift) * getPayRateValue(shift.role);
}

function getShiftsCost(shifts: IShift[]): number {
  return sum(map(getShiftCost, shifts));
}

function filterShiftsForDay(shifts: IShift[], day: Date): IShift[] {
  return filter(partial(isBlockOnDate, [day]), shifts);
}

function getShiftsCostForDay(shifts: IShift[], day: Date): number {
  const shiftsOnDay = filterShiftsForDay(shifts, day);
  return getShiftsCost(shiftsOnDay);
}

function getPeopleCost(people: IPeople): number {
  const shifts = getPeopleShifts(people);
  return getShiftsCost(shifts);
}

function getPersonCostForDay(person: IPerson, day: Date): number {
  return person.assignee.match({
    Just: () => getShiftsCostForDay(person.shifts, day),
    Nothing: () => 0,
  });
}

function getPeopleCostForDay(people: IPeople, day: Date): number {
  return people
    .map((person: IPerson) => getPersonCostForDay(person, day))
    .reduce((acc, cost) => acc + cost, 0);
}

/**
 * Calculate the total cost of all Shifts in a given Schedule
 */
function getScheduleCost(schedule: ISchedule): number {
  return getPeopleCost(schedule.people);
}

/**
 * Calculate the total cost of all Shifts in a given Schedule for a given day
 */
function getScheduleCostForDay(schedule: ISchedule, day: Date): number {
  return getPeopleCostForDay(schedule.people, day);
}

/**
 *
 * Calculate the cost of all Shifts in a given Schedule, per day
 */
type CategoryCosts = { [key in "Total" | Category]: number };

type LaborCostsPerDay = Map<string, CategoryCosts>;

function getScheduleCostPerDay(schedule: ISchedule): LaborCostsPerDay {
  return Maybe.all(schedule.startDate, schedule.endDate).match({
    Just: ([startDate, endDate]) => {
      const daysInSchedule = eachDay(startDate, endDate);
      return reduce(
        (costPerDay, day: Date) => {
          const categoryCosts: CategoryCosts = getScheduleCostPerCategoryForDay(
            schedule,
            day
          );
          const totalToday = getScheduleCostForDay(schedule, day);
          return costPerDay.set(format(day, DateFormats.YEAR_MONTH_DAY), {
            ...categoryCosts,
            Total: totalToday,
          });
        },
        new Map() as LaborCostsPerDay,
        daysInSchedule
      );
    },
    Nothing: () => new Map(),
  });
}

type CategoryShiftGroups = { [key in "Total" | Category]: IShift[] };

function getGroupedShiftsByCategory(schedule: ISchedule): CategoryShiftGroups {
  return pipe<ISchedule, IPeople, IPeople, IShift[], IGroupedShifts>(
    prop("people"),
    filter(isAssigned),
    getPeopleShifts,
    groupBy((shift: IShift) => shift.role.category)
  )(schedule) as CategoryShiftGroups;
}

function isAssigned(person: IPerson): boolean {
  return person.assignee.isJust();
}

function getGroupedPeople(schedule: ISchedule): IGroupedPeople {
  return pipe<ISchedule, IPeople, IPeople, IGroupedPeople>(
    prop("people"),
    filter(isAssigned),
    groupBy(person => getAssigneeId(person.assignee))
  )(schedule);
}

/**
 * Calculate the total cost of all Shifts in a given Schedule by category
 */
function getScheduleCostPerCategory(schedule: ISchedule): CategoryCosts {
  return pipe<ISchedule, CategoryShiftGroups, IGroupedCosts>(
    getGroupedShiftsByCategory,
    mapObjIndexed(getShiftsCost)
  )(schedule) as CategoryCosts;
}

/**
 * Calculate the total cost of all Shifts in a given Schedule by category for a given day
 */
function getScheduleCostPerCategoryForDay(
  schedule: ISchedule,
  day: Date
): CategoryCosts {
  return pipe<ISchedule, CategoryShiftGroups, IGroupedCosts>(
    getGroupedShiftsByCategory,
    mapObjIndexed((shifts: IShift[]) => getShiftsCostForDay(shifts, day))
  )(schedule) as CategoryCosts;
}

/**
 * Calculate the total cost of all Shifts in a given Schedule by person
 */
function getScheduleCostPerPerson(schedule: ISchedule): IGroupedCosts {
  return pipe<ISchedule, IGroupedPeople, IGroupedCosts>(
    getGroupedPeople,
    mapObjIndexed(getPeopleCost)
  )(schedule);
}

/**
 * Calculate the total cost of all Shifts in a given Schedule by person for a given day
 */
function getScheduleCostPerPersonForDay(
  schedule: ISchedule,
  day: Date
): IGroupedCosts {
  return pipe<ISchedule, IGroupedPeople, IGroupedCosts>(
    getGroupedPeople,
    mapObjIndexed((people: IPeople) => getPeopleCostForDay(people, day))
  )(schedule);
}

export {
  getScheduleCost,
  getScheduleCostForDay,
  getScheduleCostPerCategory,
  getScheduleCostPerCategoryForDay,
  getScheduleCostPerDay,
  getScheduleCostPerPerson,
  getScheduleCostPerPersonForDay,
  LaborCostsPerDay,
  CategoryCosts,
};
